import React from 'react';
import { useIntl } from 'react-intl';
import { Row, Col } from 'reactstrap';
import date from 'date-and-time';
import { TypedMemo } from '../../../common/types';
import { ArtistStatsCard } from '../ArtistStatsCard/ArtistStatsCard';
import { useTrackPlaylistAdditions } from '../../hooks/useTrackPlaylistAdditions';
import { useTrackPlaylistAppearances } from '../../hooks/useTrackPlaylistAppearances';
import { useEntityIdParam } from '../../hooks/useEntityIdParam';
import { useSMLDatePickerDateParam } from '../../hooks/useSMLDatePickerDateParam';
import { useTrackListeningScore } from '../../hooks/useTrackListeningScore';
import { useCanViewListeningInsights } from '../../hooks/useCanViewListeningInsights';
import { useTrackReach } from '../../hooks/useTrackReach';
import { ReachCard } from '../ReachCard/ReachCard';

interface TrackStatsRowProps {
  // No props needed - component will use hooks directly
}

const TrackStatsRowComponent: React.FC<TrackStatsRowProps> = () => {
  const intl = useIntl();
  const trackId = useEntityIdParam('track');
  const { selectedDate, rangeDays } = useSMLDatePickerDateParam();

  const {
    data: playlistAdditionsData,
    isLoading: isPlaylistAdditionsLoading,
    error: playlistAdditionsError,
  } = useTrackPlaylistAdditions({
    trackId: trackId || '',
    date: selectedDate,
  });

  const {
    data: playlistAppearancesData,
    isLoading: isPlaylistAppearancesLoading,
    error: playlistAppearancesError,
  } = useTrackPlaylistAppearances({
    trackId: trackId || '',
    date: selectedDate,
  });

  const canViewListeningInsights = useCanViewListeningInsights();

  const {
    data: listeningData,
    isLoading: isListeningLoading,
    error: listeningError,
  } = useTrackListeningScore({ trackId: trackId || '', date: selectedDate, enabled: canViewListeningInsights });

  const {
    data: reachData,
    isLoading: isReachLoading,
    error: reachError,
    refetch: refetchReach,
  } = useTrackReach({ trackId: trackId || '', date: selectedDate, rangeDays });

  // Format the selected date for display
  const formattedDate = date.format(selectedDate, 'MMM DD, YYYY');

  return (
    <Row>
      {/* Added To Playlists Score */}
      <Col xs={12} md={4} className="mb-3">
        <ArtistStatsCard
          title={intl.formatMessage({
            id: 'app.sml.track.details.added-to-playlists-score',
            defaultMessage: 'Playlist Gain Score',
          })}
          data={playlistAdditionsData?.addedToPlaylists}
          isLoading={isPlaylistAdditionsLoading}
          error={playlistAdditionsError}
          unit="score"
          date={formattedDate}
          tooltipText={intl.formatMessage({
            id: 'app.sml.track.details.added-to-playlists-score.tooltip',
            defaultMessage:
              'Shows how many times this track was added to playlists relative to all the tracked tracks. Higher scores indicate growing popularity.',
          })}
        />
      </Col>

      {/* Removed From Playlists Score */}
      <Col xs={12} md={4} className="mb-3">
        <ArtistStatsCard
          title={intl.formatMessage({
            id: 'app.sml.track.details.removed-from-playlists-score',
            defaultMessage: 'Playlist Loss Score',
          })}
          data={playlistAdditionsData?.removedFromPlaylists}
          isLoading={isPlaylistAdditionsLoading}
          error={playlistAdditionsError}
          unit="score"
          date={formattedDate}
          invertColorLogic
          tooltipText={intl.formatMessage({
            id: 'app.sml.track.details.removed-from-playlists-score.tooltip',
            defaultMessage:
              'Shows how many times this track was removed from playlists relative to all the tracked tracks. Lower scores are better.',
          })}
        />
      </Col>

      {/* Playlist Visibility Score */}
      <Col xs={12} md={4} className="mb-3">
        <ArtistStatsCard
          title={intl.formatMessage({
            id: 'app.sml.track.details.playlist-visibility-score',
            defaultMessage: 'Playlist Visibility Score',
          })}
          data={playlistAppearancesData?.appearedInPlaylists}
          isLoading={isPlaylistAppearancesLoading}
          error={playlistAppearancesError}
          unit="score"
          date={formattedDate}
          tooltipText={intl.formatMessage({
            id: 'app.sml.track.details.playlist-visibility-score.tooltip',
            defaultMessage:
              'Shows how many playlists this track appears in relative to all the tracked tracks. Indicates overall playlist presence.',
          })}
        />
      </Col>

      {/* Chart playlist placements (raw count, not a score) */}
      <Col xs={12} md={4} className="mb-3">
        <ArtistStatsCard
          title={intl.formatMessage({
            id: 'app.sml.track.details.chartPlaylistAdds',
            defaultMessage: 'Chart playlist adds',
          })}
          data={playlistAdditionsData?.chartPlaylistAdds}
          isLoading={isPlaylistAdditionsLoading}
          error={playlistAdditionsError}
          unit="adds"
          date={formattedDate}
          tooltipText={intl.formatMessage({
            id: 'app.sml.track.details.chartPlaylistAdds.tooltip',
            defaultMessage: 'How many times this track was added to chart/editorial playlists on this day.',
          })}
        />
      </Col>

      {/* Reach score (audience-weighted adds) */}
      <Col xs={12} md={4} className="mb-3">
        <ArtistStatsCard
          title={intl.formatMessage({
            id: 'app.sml.track.details.reachScore',
            defaultMessage: 'Reach score',
          })}
          data={playlistAdditionsData?.reachScore}
          isLoading={isPlaylistAdditionsLoading}
          error={playlistAdditionsError}
          unit="reach"
          date={formattedDate}
          tooltipText={intl.formatMessage({
            id: 'app.sml.track.details.reachScore.tooltip',
            defaultMessage:
              'Adds weighted by playlist audience (1 + followers/10,000 per tracked-playlist add) — not a raw follower count.',
          })}
        />
      </Col>

      {/* Audience reach (real people, not a score) */}
      <Col xs={12} md={8} className="mb-3">
        <ReachCard
          entityType="track"
          summary={reachData?.summary}
          series={reachData?.series ?? []}
          isLoading={isReachLoading}
          error={reachError}
          retry={() => {
            refetchReach().catch(console.error);
          }}
        />
      </Col>

      {canViewListeningInsights && (
        <Col xs={12} md={4} className="mb-3">
          <ArtistStatsCard
            title={intl.formatMessage({
              id: 'app.sml.track.details.listening-score-card',
              defaultMessage: 'Listening Score',
            })}
            data={listeningData?.listeningScore}
            isLoading={isListeningLoading}
            error={listeningError}
            unit="score"
            date={formattedDate}
            tooltipText={intl.formatMessage({
              id: 'app.sml.track.details.listening-score-card.tooltip',
              defaultMessage:
                'How much an anonymized sample of users is actually listening to this track, relative to all listening activity.',
            })}
          />
        </Col>
      )}
    </Row>
  );
};

export const TrackStatsRow = TypedMemo(TrackStatsRowComponent);
